// src/admin/AdminLayout.jsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    CssBaseline,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ContactsIcon from '@mui/icons-material/Contacts';
import GroupIcon from '@mui/icons-material/Group';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import FacebookIcon from '@mui/icons-material/Facebook';

// NEW icons
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { adminLogout, adminMe } from '../utils/api';

const DRAWER_WIDTH = 260;
const HEADER_BLUE = '#0f3a77';

export default function AdminLayout() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [authed, setAuthed] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const me = await adminMe();
                if (!me && !cancelled) {
                    navigate('/login');
                } else if (!cancelled) {
                    setAuthed(true);
                }
            } catch (err) {
                console.error('adminMe failed', err);
                if (!cancelled) navigate('/login');
            } finally {
                if (!cancelled) setCheckingAuth(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const navItem = (to, label, IconComponent) => (
        <ListItemButton
            component={NavLink}
            to={to}
            sx={{ '&.active': { bgcolor: 'rgba(255,255,255,0.14)' } }}
            onClick={() => setMobileOpen(false)}
        >
            <ListItemIcon>
                <IconComponent sx={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText
                primary={label}
                primaryTypographyProps={{ sx: { color: 'white' } }}
            />
        </ListItemButton>
    );

    const drawer = (
        <Box
            role="navigation"
            aria-label="Admin navigation"
            sx={{
                bgcolor: HEADER_BLUE,
                height: '100%',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 2,
                    position: 'sticky',
                    top: 0,
                    bgcolor: HEADER_BLUE,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: '1px solid rgba(255,255,255,0.15)',
                }}
            >
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, lineHeight: 1.1 }}
                >
                    Administration
                </Typography>
            </Box>

            <List sx={{ pt: 1 }}>
                {navItem('/admin/contacts', 'Contacts', ContactsIcon)}
                {navItem('/admin/visitors', 'Visitors', EventAvailableIcon)}
                {navItem('/admin/volunteers', 'Volunteers', Diversity3Icon)}
                {navItem('/admin/members', 'Members', GroupIcon)}
                {navItem('/admin/events', 'Events', EventNoteIcon)}
                {navItem('/admin/news', 'News', NewspaperIcon)}
                {navItem(
                    '/admin/meeting-minutes',
                    'Meeting Minutes',
                    DescriptionIcon
                )}
                {navItem(
                    '/admin/financial-reports',
                    'Financial Reports',
                    AccountBalanceIcon
                )}
                {navItem('/admin/open-house', 'Open House', HomeWorkIcon)}
                {navItem('/admin/facebook', 'Facebook', FacebookIcon)}
            </List>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

            <List sx={{ mt: 'auto' }}>
                <ListItemButton
                    onClick={async () => {
                        try {
                            await adminLogout();
                        } finally {
                            navigate('/'); // back to public homepage
                        }
                    }}
                >
                    <ListItemIcon>
                        <LogoutIcon sx={{ color: 'white' }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Sign out"
                        primaryTypographyProps={{ sx: { color: 'white' } }}
                    />
                </ListItemButton>
            </List>
        </Box>
    );

    if (checkingAuth && !authed) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
                <CssBaseline />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            <Box
                component="nav"
                sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
                aria-label="Admin navigation"
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: DRAWER_WIDTH,
                        },
                    }}
                >
                    {drawer}
                </Drawer>

                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: DRAWER_WIDTH,
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
                    bgcolor: '#f5f5f5',
                    minHeight: '100vh',
                }}
            >
                <Box
                    sx={{
                        display: { xs: 'flex', sm: 'none' },
                        alignItems: 'center',
                        mb: 2,
                        gap: 1,
                    }}
                >
                    <IconButton
                        edge="start"
                        aria-label="Open admin menu"
                        onClick={() => setMobileOpen(true)}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Administration
                    </Typography>
                </Box>

                <Outlet />
            </Box>
        </Box>
    );
}
